// apps/merchant-v3/src/lib/auth/AuthProvider.tsx
//
// Phase 6 Wave 1 Task 03 — Auth context provider. Composes:
//   - `useMe()` — TanStack Query bootstrap (`GET /auth/me` on mount).
//   - `login()`  — `POST /auth/login` mutation. Handles 2FA challenge
//     : when the server returns `requires2FA: true` without
//     setting cookie, the caller re-calls login with `twoFactorCode`.
//   - `logout()` — `POST /auth/logout` mutation. Clears query cache +
//     local storage on success (auth cookie cleared by server).
// WS tickets are NOT minted here. A `mintWsTicket()` helper used to sit on this
// context "for the future WS client", but the transport landed elsewhere:
// WsProvider owns one socket and `connectWs` (packages/ui) mints its own
// single-use ticket per connect. Nothing ever called the context helper, so it
// was a third unowned path to POST /auth/ws-ticket — removed 2026-07-30.
//
// On mount the provider also installs a 401 unauthorized-handler with
// the api/client (`setUnauthorizedHandler`). When a request 401s and
// the single-flight refresh fails, the handler calls `navigate()` to
// `/login?returnUrl=<current-path>` (deep-link preservation).
//
// vg-binding: PLAN:task-03
// vg-binding: P6.D-02
// vg-binding: P6.D-125
// vg-binding: P6.D-129
// vg-binding: P6.D-172
// vg-binding: P6.D-205
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:response-envelope

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  apiClient,
  ApiError,
  setUnauthorizedHandler,
} from '@/lib/api/client';
import { useMe, ME_QUERY_KEY } from '@/lib/auth/useMe';
import { useUserStore } from '@/store/user';
import type {
  LoginRequest,
  LoginResponse,
  MeUser,
} from '@/types/auth';
import { mapLoginUserToUser } from '@/types/auth';

// --- context shape --------------------------------------------------------

export interface LoginResult {
  /** True when server requested a 2FA TOTP code. LoginPage
   *  swaps the form state to the TOTP entry view when this is true. */
  requires2FA: boolean;
  /** Server-issued opaque challenge token. Required on the follow-up
   *  POST /auth/verify-2fa call when requires2FA === true. */
  challengeToken?: string;
  /** Optional server-supplied next URL — wins over client `returnUrl`. */
  redirectTo?: string;
}

export interface AuthContextValue {
  /** Current user (mirrors `useMe` data). null = unauthenticated.
   *  Identical to `useMe().data` but without the loading flag for
   *  consumers that only care about the resolved value. */
  user: MeUser | null | undefined;
  /** True while the initial /auth/me bootstrap is pending. */
  isLoading: boolean;
  /** True when the bootstrap has resolved and user is null. */
  isUnauthenticated: boolean;
  /** Submit credentials → resolves `requires2FA` flag for caller. */
  login: (req: LoginRequest) => Promise<LoginResult>;
  /** Submit the 2FA challenge response. Called after `login()` returned
   *  `requires2FA: true`. Resolves to `LoginResult` (requires2FA:false
   *  on success; throws ApiError on bad code). */
  verify2FA: (challengeToken: string, code: string) => Promise<LoginResult>;
  /** Best-effort revoke + cache clear. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// --- provider -------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useUserStore((s) => s.setUser);

  // Bootstrap + cache the current user.
  const meQuery = useMe();

  // 401 single-flight refresh failure → redirect to /login. Installed
  // here so the navigator and the api/client share one redirect path.
  useEffect(() => {
    setUnauthorizedHandler((returnUrl) => {
      const currentPath =
        typeof window !== 'undefined' ? window.location.pathname : '';
      const isPublicAuthRoute =
        currentPath === '/login' ||
        currentPath === '/forgot-password' ||
        currentPath === '/reset-password' ||
        // Phase 6 Wave 15a Task 54 — register / verify-email
        // public flow: AuthProvider's 401 redirect MUST NOT fire here or
        // /auth/me 401 (no session yet) bounces a brand-new user back to
        // /login mid-registration.
        currentPath === '/register' ||
        currentPath === '/verify-email';

      if (isPublicAuthRoute) {
        setUser(null);
        return;
      }

      // Preserve deep link.
      const target =
        returnUrl &&
        returnUrl !== '/' &&
        returnUrl !== '/login' &&
        !returnUrl.startsWith('/login?')
          ? '/login?returnUrl=' + encodeURIComponent(returnUrl)
          : '/login';
      // Clear local cache so the next mount fetches /auth/me fresh.
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
      setUser(null);
      navigate(target, { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, queryClient, setUser]);

  // --- mutations ----------------------------------------------------------

  const loginMutation = useMutation<LoginResponse['data'], ApiError, LoginRequest>({
    mutationFn: async (req) =>
      apiClient.post<LoginResponse['data']>('/api/v1/auth/login', req),
    onSuccess: async (data) => {
      if (!data.requires2FA) {
        // Mirror returned user immediately so post-login pages render
        // without waiting for the next /auth/me round-trip.
        setUser(mapLoginUserToUser(data.user));
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      }
    },
  });

  const logoutMutation = useMutation<void, ApiError>({
    mutationFn: async () => {
      try {
        await apiClient.post<null>('/api/v1/auth/logout', {});
      } catch (err) {
        // Logout is best-effort — swallow auth errors. Cookie still
        // cleared by server when token was already expired.
        if (err instanceof ApiError && err.status === 401) return;
        throw err;
      }
    },
    onSettled: async () => {
      // Hard-clear local state regardless of network outcome.
      setUser(null);
      // dbg-20260602 iter10 — nuke the ENTIRE query cache, not just the
      // /auth/me slice. Removing only ME_QUERY_KEY left per-user data
      // (my-products, orders, billing, …) cached in memory, so logging out
      // and signing in as another account in the SAME tab showed the
      // previous user's products until a full F5 reload dropped the cache.
      queryClient.clear();
      try {
        // Clear any non-essential auth-scoped local data per task
        // validation criterion "localStorage cleared on logout".
        if (typeof localStorage !== 'undefined') {
          // Selectively clear merchant-v3.* keys — keep persisted
          // sidebar collapse so UX survives logout/login cycles, but
          // drop everything auth-scoped.
          const dropPrefixes = ['merchant-v3.auth.', 'merchant-v3.cache.'];
          const toDrop: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && dropPrefixes.some((p) => k.startsWith(p))) toDrop.push(k);
          }
          toDrop.forEach((k) => localStorage.removeItem(k));
        }
      } catch {
        // localStorage may be unavailable (private mode); ignore.
      }
    },
  });

  // --- helpers ------------------------------------------------------------

  const login = useCallback<AuthContextValue['login']>(
    async (req) => {
      const data = await loginMutation.mutateAsync(req);
      return {
        requires2FA: data.requires2FA,
        challengeToken: (data as { challengeToken?: string }).challengeToken,
        redirectTo: data.redirectTo,
      };
    },
    [loginMutation],
  );

  // BUG-DEBUG-B2-S2 (2026-05-25): verify-2fa flow. After /auth/login
  // returns requires2FA:true, the FE must POST the user-supplied 6-digit
  // code to /auth/verify-2fa together with the server-issued
  // challengeToken. On success, the BE rotates auth cookies + returns
  // the user envelope, same shape as a no-2FA login success.
  const verify2FA = useCallback<AuthContextValue['verify2FA']>(
    async (challengeToken, code) => {
      const data = await apiClient.post<LoginResponse['data']>(
        '/api/v1/auth/verify-2fa',
        { challengeToken, code },
      );
      if (!data.requires2FA) {
        setUser(mapLoginUserToUser(data.user));
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      }
      return {
        requires2FA: data.requires2FA,
        challengeToken: (data as { challengeToken?: string }).challengeToken,
        redirectTo: data.redirectTo,
      };
    },
    [queryClient, setUser],
  );

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  // --- context value ------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isUnauthenticated:
        !meQuery.isLoading &&
        (meQuery.data == null ||
          (meQuery.error instanceof ApiError && meQuery.error.status === 401)),
      login,
      verify2FA,
      logout,
    }),
    [
      meQuery.data,
      meQuery.isLoading,
      meQuery.error,
      login,
      verify2FA,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// --- hook -----------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

export default AuthProvider;
